import Head from 'next/head';
import React from 'react';
import styled from 'styled-components';
import { BlogImage, CodeBlock, SectionBreak } from 'components/blog';
import BlogPostList from 'components/blog/list';
import PostInfo from 'components/blog/postInfo';
import Subtitle from 'components/blog/subtitle';
import TagRow from 'components/blog/tagRow';
import Title from 'components/blog/title';
import Body from 'components/common/body';
import { BlogPostContentType } from 'constants/blog';
import { makePageTitle } from 'constants/strings';
import { makePostUrl } from 'constants/urls';
import ContactMe from 'containers/contactMe';
import { mq } from 'utils/responsive';
import type { BlogPostContent, BlogPost as BlogPostType } from './types';

const BlogPostDiv = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const FullWidthDiv = styled.div`
  width: 100%;
  display: flex;
  justify-content: left;
  align-items: left;
`;

// For title, subtitle and post info (so it is responsive on mobile)
const PostMetadataBox = styled.div`
  padding: 15px 40px;
  width: 100%;

  ${mq.ltsm`
    padding: 15px 10px;
  `}
`;

interface BlogPostProps {
  id: string;
  path?: string;
  getBlogPost: (id: string) => BlogPostType;
}

const BlogPost = ({ id, path, getBlogPost }: BlogPostProps) => {
  const post = getBlogPost(id);

  const getPostContentComponent = ({
    type,
    content,
    idx,
  }: BlogPostContent & { idx: number }): React.ReactNode => {
    switch (type) {
      case BlogPostContentType.TITLE:
        return (
          <FullWidthDiv key={idx}>
            <PostMetadataBox>
              <Title>{content}</Title>
            </PostMetadataBox>
          </FullWidthDiv>
        );
      case BlogPostContentType.SUBTITLE:
        return (
          <FullWidthDiv key={idx}>
            <PostMetadataBox>
              <Subtitle>{content}</Subtitle>
            </PostMetadataBox>
          </FullWidthDiv>
        );
      case BlogPostContentType.POST_INFO:
        return (
          <FullWidthDiv key={idx}>
            <PostMetadataBox>
              <PostInfo>{content}</PostInfo>
              <TagRow tags={post.tags} />
            </PostMetadataBox>
          </FullWidthDiv>
        );
      case BlogPostContentType.TEXT:
        return (
          <FullWidthDiv key={idx}>
            <Body margin="7px 0">{content}</Body>
          </FullWidthDiv>
        );
      case BlogPostContentType.TEXT_IT:
        return (
          <FullWidthDiv key={idx}>
            <Body it>{content}</Body>
          </FullWidthDiv>
        );
      case BlogPostContentType.TEXT_LEFT_BORDER:
        return (
          <FullWidthDiv key={idx}>
            <Body it fontStyle="normal">
              {content}
            </Body>
          </FullWidthDiv>
        );
      case BlogPostContentType.NORMAL_IMAGE:
      case BlogPostContentType.MEDIUM_IMAGE:
      case BlogPostContentType.FULL_IMAGE:
        return <BlogImage alt="Blog Image" type={type} src={content as string} key={idx} />;
      case BlogPostContentType.NUMBERED_LIST:
        return (
          <FullWidthDiv key={idx}>
            <FullWidthDiv>
              <BlogPostList numbered>{content as React.ReactNode[]}</BlogPostList>
            </FullWidthDiv>
          </FullWidthDiv>
        );
      case BlogPostContentType.LIST:
        return (
          <FullWidthDiv key={idx}>
            <BlogPostList>{content as React.ReactNode[]}</BlogPostList>
          </FullWidthDiv>
        );
      case BlogPostContentType.YOUTUBE_VIDEO:
        return <FullWidthDiv key={idx}>TODO:</FullWidthDiv>;
      case BlogPostContentType.SECTION_BREAK:
        return (
          <FullWidthDiv key={idx}>
            <SectionBreak />
          </FullWidthDiv>
        );
      case BlogPostContentType.CODE_BLOCK:
        return (
          <FullWidthDiv>
            <CodeBlock content={content as string[]} />
          </FullWidthDiv>
        );
      case BlogPostContentType.CONTACT_ME_FORM:
        return (
          <FullWidthDiv>
            <ContactMe />
          </FullWidthDiv>
        );
      default:
        return null;
    }
  };

  const pageTitle = makePageTitle(post.title);
  const pageUrl = makePostUrl(post.id, path);

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={post.subtitle} />

        {/* Google / Search Engine Tags */}
        <meta itemProp="name" content={pageTitle} />
        <meta itemProp="description" content={post.subtitle} />
        <meta itemProp="image" content={post.previewImage} />

        {/* Facebook Meta Tags */}
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={post.subtitle} />
        <meta property="og:image" content={post.previewImage} />

        {/* Twitter Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={post.subtitle} />
        <meta name="twitter:image" content={post.previewImage} />

        <meta name="description" content={post.subtitle} />
        <meta property="url" content={pageUrl} />
        <meta property="og:image" content={post.previewImage} />
        <meta name="twitter:image" content={post.previewImage} />
      </Head>
      <BlogPostDiv>
        {post.content.map((content, idx) => getPostContentComponent({ ...content, idx }))}
      </BlogPostDiv>
    </>
  );
};

export default BlogPost;
